// ---------------------------------------------------------------------------
// TerminologySelectionCard — pure UI for the terminology-selection EXCEPTION
// path. House style copied from approval-card.tsx (rounded-lg border bg-card
// shell, header/body/footer sections, data-testid'd buttons).
//
// Plan: docs/superpowers/plans/terminology-selection-ui-plan.md (Task 2).
//
// Renders the Task-1 `TerminologySelectionModel` (buildTerminologySelectionModel
// in ../services/terminology-selection-model.ts): one row per undefined term
// that has candidates, each candidate as a clickable chip. This component is
// PURE UI — it takes the model plus injected `onSelect`/`onDismiss` callbacks
// and knows nothing about the store, the server, or project metadata (Task 3
// wires those). It never invents content: every term/id/kind/score rendered
// comes verbatim from the model.
// ---------------------------------------------------------------------------

import type {
  TerminologyKind,
} from '@cogo/validator/terminology'
import type {
  TerminologySelectionCandidate,
  TerminologySelectionModel,
  TerminologySelectionTerm,
} from '../services/terminology-selection-model'

interface TerminologySelectionCardProps {
  model: TerminologySelectionModel | null
  onSelect: (term: TerminologySelectionTerm, candidate: TerminologySelectionCandidate) => void
  onDismiss: () => void
  /**
   * Task 5 (Plan B3) — scopes declared by the `pkg.terminologySurface.scope`
   * of any `TEMPLATE_REGISTRY` entry. When a candidate's own `scope` is a
   * member of this set, the card renders an extra "템플릿 용어" badge next
   * to it — PURE presentation, no diagnosis change. Deliberately a plain
   * `Set<string>` prop rather than an import of `TEMPLATE_REGISTRY` itself:
   * this card stays a leaf UI component with no registry/store/network
   * dependency of its own; the render site (chat-messages.tsx, which
   * already imports `TEMPLATE_REGISTRY` for the sibling `template-proposal`
   * case) computes the set once and passes it down, so this file adds zero
   * incremental bundle weight. Optional — omitting it (or passing an empty
   * set) simply never renders the badge, so every existing caller/test
   * keeps working unchanged.
   */
  templateScopes?: Set<string>
}

// Korean-first, hardcoded — matches the house pattern for exception-path
// chat bubbles in this surface (displayWorklogTitle/displayWorklogEntryLabel
// in shell/ide-v2/components/chat-messages.tsx hardcode Korean directly
// rather than routing through the shell-chrome locale table, since this is
// a narrow EXCEPTION-path card, not persistent IDE chrome).
const KIND_LABELS: Record<TerminologyKind, string> = {
  'element-type': '요소',
  action: '동작',
  event: '이벤트',
  'flow-trigger': '트리거',
  'flow-step': '단계',
  'surface-term': '용어',
  'application-term': '앱 용어',
  // Coverage fill (2026-08-14) — two new platform kinds.
  'param-key': '파라미터',
  'expression-prefix': '표현식 접두사',
}

function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`
}

export function TerminologySelectionCard({ model, onSelect, onDismiss, templateScopes }: TerminologySelectionCardProps) {
  // Defense-in-depth: buildTerminologySelectionModel already excludes
  // zero-candidate terms, but this component does not assume that — it
  // guards its own render so a candidate-less row can never appear.
  const rows = (model?.terms ?? []).filter((term) => term.candidates.length > 0)
  if (rows.length === 0) return null

  return (
    <div className="my-2 rounded-lg border bg-card" data-testid="terminology-selection-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
        <span className="text-sm font-semibold">무슨 뜻인지 확실하지 않아요</span>
        <span className="text-xs text-muted-foreground">
          {rows.length}개 용어
        </span>
      </div>

      {/* Rows: one per candidate-bearing undefined term */}
      <div className="p-3 space-y-3">
        {rows.map((term, termIndex) => (
          <div key={term.term} data-testid={`terminology-selection-term-${termIndex}`}>
            <div className="text-xs font-medium text-muted-foreground mb-1">{term.term}</div>
            <div className="flex flex-wrap gap-1.5">
              {term.candidates.map((candidate, candidateIndex) => (
                <button
                  key={`${candidate.id}-${candidate.kind}`}
                  type="button"
                  onClick={() => onSelect(term, candidate)}
                  className="rounded border bg-background px-2 py-1 text-xs hover:bg-accent"
                  data-testid={`terminology-selection-candidate-${termIndex}-${candidateIndex}`}
                >
                  <span className="font-mono">{candidate.id}</span>
                  <span className="ml-1.5 text-muted-foreground">{KIND_LABELS[candidate.kind]}</span>
                  <span className="ml-1.5 text-muted-foreground">{formatScore(candidate.score)}</span>
                  {templateScopes?.has(candidate.scope) && (
                    <span
                      className="ml-1.5 rounded bg-violet-100 px-1 py-0.5 text-[10px] font-medium text-violet-800"
                      data-testid={`terminology-selection-badge-${termIndex}-${candidateIndex}`}
                    >
                      템플릿 용어
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 px-4 py-2 border-t bg-muted/30">
        <button
          type="button"
          className="px-3 py-1 text-sm font-medium rounded border hover:bg-accent"
          onClick={onDismiss}
          data-testid="terminology-selection-dismiss"
        >
          해당 없음 / 나중에 정의
        </button>
      </div>
    </div>
  )
}
